import moment from "moment";
import { UserAgencyModelType, UserModelType } from "../../types/user-types";
import CreateManualSubscriptionModel from "../../models/create-manual-subscription-model";
import { useEffect, useState } from "react";
import Button from "../button/Button";
import SingleUser from "./single-user";
import CreateNewUser from "../../models/create-new-user";
import axiosInstance from "../../utils/axios-instance";
import PaginationMenu from "../pagination/paginationMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faTrash } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { ParseErrors } from "../../utils/parse-errors";
import SuretyModel from "../../models/surety-model";
import ExportConversationModel from "../../models/export-conversations-model";
import clsx from "clsx";

interface ISingleAgencyDetails {
  item: UserAgencyModelType;
}

const SingleAgencyDetails = ({ item }: ISingleAgencyDetails) => {
  const [users, setUsers] = useState<UserModelType[]>(item.users);
  const [isViewed, setIsViewed] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletePopup, setIsDeletePopup] = useState(false);
  const [isSubscriptionModel, setIsSubscriptionModel] = useState(false);
  const [isExportModel, setIsExportModel] = useState(false);
  const [isUserModel, setIsUserModel] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [limit] = useState(10);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const getMoreUsers = async () => {
      try {
        const res = await axiosInstance.get(
          `/user/agency/${item.id}?limit=${limit}&page=${page}`
        );
        setTotalUsers(res.data.totalCount);
        setUsers(res.data.data);
      } catch (error) {
        console.log(error);
      }
    };

    item.id && page && getMoreUsers();
  }, [page, limit, item.id]);

  const HandlePageChange = (page: number) => {
    setPage(page);
  };

  const toggleExportModel = () => setIsExportModel((prev) => !prev);
  const toggleSubscriptionModel = () => setIsSubscriptionModel((prev) => !prev);
  const toggleViewModel = () => setIsViewed((prev) => !prev);
  const toggleUserModel = () => setIsUserModel((prev) => !prev);
  const toggleDeleteSuretyModel = () => setIsDeletePopup((prev) => !prev);

  const handleDeleteAgency = async () => {
    setIsDeleting(true);
    try {
      const res = await axiosInstance.delete(
        `/auth/super/delete/agency/${item.id}`
      );
      toast.success(res.data.message);
      setIsDeleting(false);
      toggleDeleteSuretyModel();
    } catch (error) {
      console.log(error);
      setIsDeleting(false);
      toast.error(ParseErrors(error));
    }
  };

  const status = item.subscription?.status;

  return (
    <div className="w-full">
      <div className="cursor-pointer grid grid-cols-6 gap-x-3 text-lightBlack bg-white hover:bg-gray-50 transition-colors py-4 px-6 border-b border-gray-100 items-center">
        <div onClick={toggleViewModel} className="font-medium text-[14.5px] text-start capitalize">
          {item.name}
        </div>
        <div onClick={toggleViewModel} className="text-sm text-gray-600">
          {item.email}
        </div>
        <div onClick={toggleViewModel} className="text-sm">
          {item.subscription?.totalUsers || 0}
        </div>
        <div className="text-sm capitalize">
          {item.subscription?.type ? (
            item.subscription?.type === "manual" ? (
              <button
                className="text-blue hover:underline"
                onClick={toggleSubscriptionModel}
              >
                Manual ({item?.subscription?.plan})
              </button>
            ) : (
              `${item.subscription?.type} (${item?.subscription?.plan})`
            )
          ) : (
            <button
              className="text-blue hover:underline"
              onClick={toggleSubscriptionModel}
            >
              Create Subscription
            </button>
          )}
        </div>
        <div onClick={toggleViewModel} className="text-sm capitalize">
          <span className={clsx(
            "inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium",
            status === "paid" && "bg-green-50 text-green-600",
            status === "unpaid" && "bg-red-50 text-red-600",
            !status && "bg-gray-50 text-gray-500"
          )}>
            {status || "None"}
          </span>
        </div>
        <div className="text-sm text-end flex items-center justify-end gap-3">
          <span className="text-gray-500">{moment(item.createdAt).format("DD MMM YYYY")}</span>
          <button
            className="h-8 w-8 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition"
            onClick={toggleExportModel}
            aria-label="Export"
          >
            <FontAwesomeIcon icon={faDownload} className="text-xs text-lightBlack" />
          </button>
          <button
            onClick={toggleDeleteSuretyModel}
            disabled={isDeleting}
            className="h-8 w-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center transition disabled:opacity-50"
            aria-label="Delete"
          >
            <FontAwesomeIcon icon={faTrash} className="text-xs" />
          </button>
        </div>
      </div>

      {isViewed ? (
        <div className="w-full py-5 bg-gray-50/50 border-b border-gray-100">
          <div className="w-[95%] mx-auto">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-base text-lightBlack font-semibold">
                Total Users{" "}
                <span className="text-blue">
                  ({totalUsers ? totalUsers : item.users.length})
                </span>
              </h2>
              <div className="w-auto">
                <Button className="!w-auto !px-5" onClick={toggleUserModel}>+ Add User</Button>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              {users.map((u) => {
                return <SingleUser item={u} key={u.id} />;
              })}

              {page ? (
                <PaginationMenu
                  totalCount={totalUsers}
                  pageSize={limit}
                  currentPage={page}
                  onPageChange={HandlePageChange}
                />
              ) : users.length >= limit ? (
                <div className="w-full text-center mt-4">
                  <button
                    className="text-sm text-blue hover:underline font-medium"
                    onClick={() => setPage(1)}
                  >
                    Show More
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {isExportModel ? (
        <ExportConversationModel toggleModel={toggleExportModel} id={item.id} />
      ) : null}
      {isDeletePopup ? (
        <SuretyModel
          onClick={handleDeleteAgency}
          isLoading={isDeleting}
          toggleModel={toggleDeleteSuretyModel}
        />
      ) : null}
      {isUserModel ? (
        <CreateNewUser
          toggleUserModel={toggleUserModel}
          agencyId={item.id}
          HandlePageChange={HandlePageChange}
        />
      ) : null}
      {isSubscriptionModel ? (
        <CreateManualSubscriptionModel
          isSubscription={item.subscription}
          toggleSubscriptionModel={toggleSubscriptionModel}
          agencyId={item.id}
        />
      ) : null}
    </div>
  );
};

export default SingleAgencyDetails;
